import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../base/Modal';
import Toast from '../base/Toast';
import { useAuth } from '../../context/AuthContext';
import { usersService } from '../../services/users.service';

const PREF_MAP: Record<string, string> = {
  emailNotifications: 'ticket_updated',
  escalationAlerts: 'ticket_escalated',
  ticketAssignments: 'ticket_assigned',
  weeklyDigest: 'system_alert',
  clientUpdates: 'contract_renewal',
};

interface ProfileEditModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ProfileEditModal({ isOpen, onClose }: ProfileEditModalProps) {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    jobTitle: '',
    role: '',
    emailNotifications: true,
    escalationAlerts: true,
    ticketAssignments: true,
    weeklyDigest: false,
    clientUpdates: true,
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (user && isOpen) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        jobTitle: user.role_name || '',
        role: user.role_name || '',
      }));

      usersService.getNotificationPreferences(user.id).then((prefs) => {
        const mapped: Partial<typeof formData> = {};
        for (const [formKey, typeKey] of Object.entries(PREF_MAP)) {
          const pref = prefs.find(p => p.notification_type_key === typeKey);
          if (pref) {
            mapped[formKey as keyof typeof mapped] = pref.send_email as never;
          }
        }
        setFormData(prev => ({ ...prev, ...mapped }));
      }).catch((err) => console.error('Failed to load notification preferences:', err));
    }
  }, [user, isOpen]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('profile.err.nameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('profile.err.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('profile.err.emailInvalid');
    }

    if (formData.currentPassword) {
      if (!formData.newPassword) {
        newErrors.newPassword = t('profile.err.newPasswordRequired');
      } else if (formData.newPassword.length < 8) {
        newErrors.newPassword = t('profile.err.passwordMin8');
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = t('profile.err.confirmPassword');
      } else if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = t('profile.err.passwordMismatch');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !user) {
      return;
    }

    setSaving(true);
    try {
      const updatePayload: Record<string, string> = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
      };

      if (formData.currentPassword && formData.newPassword) {
        updatePayload.password = formData.newPassword;
      }

      await usersService.update(user.id, updatePayload);

      const notifPrefs = Object.entries(PREF_MAP).map(([formKey, typeKey]) => ({
        notification_type_key: typeKey,
        send_email: formData[formKey as keyof typeof formData] as boolean,
        send_in_app: true,
        send_sms: false,
      }));
      await usersService.saveNotificationPreferences(user.id, notifPrefs);

      await refreshUser();
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));
      setToastMessage(t('profile.toast.updated'));
      setToastType('success');
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        onClose();
      }, 2000);
    } catch {
      setToastMessage(t('profile.toast.updateFailed'));
      setToastType('error');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        jobTitle: user.role_name || '',
        role: user.role_name || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
    }
    setErrors({});
    onClose();
  };

  const handleChangePhoto = () => {
    setToastMessage(t('profile.toast.comingSoon'));
    setToastType('success');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleCancel}
        title={t('profile.editTitle')}
        size="lg"
        footer={
          <>
            <button
              onClick={handleCancel}
              className="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-700 hover:bg-neutral-50 text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-cysol-red hover:bg-red-700 text-white text-sm font-bold transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? t('common.saving') : t('common.saveChanges')}
            </button>
          </>
        }
      >
        <div className="space-y-6">
          {/* Avatar Section */}
          <div className="flex items-center gap-6 pb-6 border-b border-neutral-200">
            <img
              src={user?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=0284c7&color=fff`}
              alt={t('profile.imageAlt')}
              className="w-20 h-20 rounded-full object-cover border-2 border-neutral-200"
            />
            <div>
              <button
                onClick={handleChangePhoto}
                className="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-700 hover:bg-neutral-50 text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
              >
                {t('profile.changePhoto')}
              </button>
              <p className="text-xs text-neutral-500 mt-2">{t('profile.photoHint')}</p>
            </div>
            <div className="ml-auto">
              <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-cysol-red/10 text-cysol-red text-xs font-bold rounded-full">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-shield-star-line text-xs"></i>
                </div>
                {formData.role}
              </span>
            </div>
          </div>

          {/* Personal Info Section */}
          <div>
            <h3 className="text-base font-bold text-neutral-900 mb-4">{t('profile.sectionPersonal')}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-2">
                  {t('users.form.fullName')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className={`w-full px-4 py-2.5 border ${errors.name ? 'border-red-500' : 'border-neutral-300'
                    } rounded-lg text-sm focus:ring-2 focus:ring-cysol-red/60 focus:border-cysol-red outline-none transition-all`}
                />
                {errors.name && (
                  <p className="text-xs text-red-500 mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-2">
                  {t('common.email')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className={`w-full px-4 py-2.5 border ${errors.email ? 'border-red-500' : 'border-neutral-300'
                    } rounded-lg text-sm focus:ring-2 focus:ring-cysol-red/60 focus:border-cysol-red outline-none transition-all`}
                />
                {errors.email && (
                  <p className="text-xs text-red-500 mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-2">
                  {t('profile.phoneNumber')}
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-cysol-red/60 focus:border-cysol-red outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-2">
                  {t('profile.jobTitle')}
                </label>
                <input
                  type="text"
                  value={formData.jobTitle}
                  onChange={(e) => handleChange('jobTitle', e.target.value)}
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-cysol-red/60 focus:border-cysol-red outline-none transition-all"
                />
              </div>
            </div>
          </div>

          {/* Notification Preferences Section */}
          <div className="pt-6 border-t border-neutral-200">
            <h3 className="text-base font-bold text-neutral-900 mb-4">{t('profile.sectionNotifications')}</h3>
            <div className="space-y-3">
              {[
                { key: 'emailNotifications', labelKey: 'profile.prefs.emailNotifications', descKey: 'profile.prefs.emailNotificationsDesc' },
                { key: 'escalationAlerts', labelKey: 'profile.prefs.escalationAlerts', descKey: 'profile.prefs.escalationAlertsDesc' },
                { key: 'ticketAssignments', labelKey: 'profile.prefs.ticketAssignments', descKey: 'profile.prefs.ticketAssignmentsDesc' },
                { key: 'weeklyDigest', labelKey: 'profile.prefs.weeklyDigest', descKey: 'profile.prefs.weeklyDigestDesc' },
                { key: 'clientUpdates', labelKey: 'profile.prefs.clientUpdates', descKey: 'profile.prefs.clientUpdatesDesc' }
              ].map((item) => (
                <label
                  key={item.key}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-neutral-50 cursor-pointer transition-all"
                >
                  <input
                    type="checkbox"
                    checked={formData[item.key as keyof typeof formData] as boolean}
                    onChange={(e) => handleChange(item.key, e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-cysol-red border-neutral-300 rounded focus:ring-2 focus:ring-cysol-red/60 cursor-pointer"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-neutral-900">{t(item.labelKey)}</p>
                    <p className="text-xs text-neutral-500">{t(item.descKey)}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Security Section */}
          <div className="pt-6 border-t border-neutral-200">
            <h3 className="text-base font-bold text-neutral-900 mb-4">{t('profile.sectionSecurity')}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-neutral-700 mb-2">
                  {t('profile.currentPassword')}
                </label>
                <input
                  type="password"
                  value={formData.currentPassword}
                  onChange={(e) => handleChange('currentPassword', e.target.value)}
                  placeholder={t('profile.currentPasswordPlaceholder')}
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-cysol-red/60 focus:border-cysol-red outline-none transition-all"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-2">
                    {t('common.newPassword')}
                  </label>
                  <input
                    type="password"
                    value={formData.newPassword}
                    onChange={(e) => handleChange('newPassword', e.target.value)}
                    disabled={!formData.currentPassword}
                    className={`w-full px-4 py-2.5 border ${errors.newPassword ? 'border-red-500' : 'border-neutral-300'
                      } rounded-lg text-sm focus:ring-2 focus:ring-cysol-red/60 focus:border-cysol-red outline-none transition-all disabled:bg-neutral-100 disabled:cursor-not-allowed`}
                  />
                  {errors.newPassword && (
                    <p className="text-xs text-red-500 mt-1">{errors.newPassword}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-2">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    disabled={!formData.currentPassword}
                    className={`w-full px-4 py-2.5 border ${errors.confirmPassword ? 'border-red-500' : 'border-neutral-300'
                      } rounded-lg text-sm focus:ring-2 focus:ring-cysol-red/60 focus:border-cysol-red outline-none transition-all disabled:bg-neutral-100 disabled:cursor-not-allowed`}
                  />
                  {errors.confirmPassword && (
                    <p className="text-xs text-red-500 mt-1">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>
              <p className="text-xs text-neutral-500">
                {t('profile.passwordHintMin8')}
              </p>
            </div>
          </div>
        </div>
      </Modal>

      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
        />
      )}
    </>
  );
}
